import React, { useEffect, useState } from "react";
import { ROUTES } from "constant";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ErrorIcon from "@mui/icons-material/Error";
import { useAuthData } from "modules/Authentication/hooks/useAuthData";

type Props = {};

export const ForgetPasswordsForm = (props: Props) => {
  const { onLogin, loading, error, confrimPasswordChange } = useAuthData();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string[]>([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [success, setSuccess] = useState(false); // To show success message
  const [oobCode, setOobCode] = useState<string | null>(null); // Store oobCode for password reset

  const navigate = useNavigate();
  const location = useLocation();

  // Extract the oobCode from the URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const oobCodeParam = searchParams.get("oobCode");
    if (oobCodeParam) {
      setOobCode(oobCodeParam);
    }
  }, [location]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "password") {
      setPassword(value);
      validatePassword(value, confirmPassword);
    } else if (name === "confirmPassword") {
      setConfirmPassword(value);
      validatePassword(password, value);
    }
  };

  const validatePassword = (password: string, confirmPassword: string) => {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push("Minimum 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("At least 1 uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("At least 1 lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("At least 1 number");
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push("At least 1 special symbol");
    }
    if (password !== confirmPassword) {
      errors.push("Passwords won’t match");
    }

    setPasswordError(errors);
    if (password === confirmPassword) {
      setConfirmPasswordError("");
    } else {
      setConfirmPasswordError("");
    }
  };

  // const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
  //   event.preventDefault();
  //   validatePassword(password, confirmPassword);
  //   if (passwordError.length === 0 && password === confirmPassword) {
  //     handleNavigateLogin();
  //   }
  // };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    validatePassword(password, confirmPassword);

    if (passwordError.length === 0 && password === confirmPassword && oobCode) {
      const success = await confrimPasswordChange(oobCode, password);
      if (success) {
        alert("Password reset successful");
        setSuccess(true);
        handleNavigateLogin();
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNavigateLogin = () => navigate(ROUTES.LOGIN);

  return (
    <React.Fragment>
      <form onSubmit={handleSubmit}>
        <Grid item sx={{ cursor: "pointer" }} onClick={handleBack}>
          <ArrowBackIcon />
        </Grid>
        <Grid mt={3} container spacing={3}>
          <Grid item>
            <Box
              mt={3}
              fontSize={{ xs: "20.76px", md: "28px" }}
              fontWeight={{ xs: "600", md: "500" }}
              color="black"
            >
              Reset Password
            </Box>
            <Typography
              fontSize={{ xs: "15px", md: "18px" }}
              fontWeight={{ xs: "400", md: "400" }}
              color="#6D6D6D"
              display="flex"
              gap={1}
            >
              lorem ipsum lorem ipsum
            </Typography>
          </Grid>
          <Grid mt={3} item xs={12} display={"flex"} direction={"column"}>
            <Grid item xs={12}>
              <TextField
                type={showPassword ? "text" : "password"}
                name="password"
                variant="outlined"
                fullWidth
                placeholder="Enter your password"
                error={!!passwordError}
                value={password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          width: { xs: "11.86px", md: "20px" },
                          height: { xs: "16px", md: "20px" }, // you can adjust height as needed
                          color: "#6D6D6D",
                        }}
                      >
                        <svg
                          style={{
                            width: "100%",
                            height: "100%",
                          }}
                          color="#6D6D6D"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            opacity="0.7"
                            d="M8.8125 10.75L7.75 11.7812C7.625 11.9375 7.4375 12 7.25 12H6V13.25C6 13.6875 5.65625 14 5.25 14H4V15.25C4 15.6875 3.65625 16 3.25 16H0.75C0.3125 16 0 15.6875 0 15.25V12.75C0 12.5625 0.0625 12.375 0.21875 12.2188L5.25 7.1875C5.0625 6.65625 5 6.09375 5 5.5C5 2.46875 7.4375 0 10.5 0C13.5312 0 16 2.46875 16 5.5C16 8.5625 13.5312 11 10.5 11C9.90625 11 9.34375 10.9375 8.8125 10.75ZM11.75 5.5C12.4375 5.5 13 4.96875 13 4.25C13 3.5625 12.4375 3 11.75 3C11.0312 3 10.5 3.5625 10.5 4.25C10.5 4.96875 11.0312 5.5 11.75 5.5Z"
                            fill="#6D6D6D"
                          />
                        </svg>
                      </Box>
                    </InputAdornment>
                  ),

                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        onClick={togglePasswordVisibility}
                        sx={{ cursor: "pointer" }}
                      >
                        <Box component="span" sx={{ color: "#6D6D6D" }}>
                          {showPassword ? (
                            <VisibilityIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          ) : (
                            <VisibilityOffIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",
                  "& .MuiInputBase-root": {
                    height: { xs: "35.39px", md: "48px" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#6D6D6D",
                    fontSize: { xs: "12px", md: "16px" },
                    fontWeight: "500",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderTopLeftRadius: "12px",
                      borderTopRightRadius: "12px",
                      borderBottomRightRadius: "0px",
                      borderBottomLeftRadius: "0px",
                      borderColor: passwordError.length
                        ? "red"
                        : "#E7E7E7 !important", // This ensures the border is red only if there's an error
                    },
                    "&:hover fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "& input": {
                      color: passwordError.length ? "red" : "inherit",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                type={showPassword ? "text" : "password"}
                name="confirmPassword"
                variant="outlined"
                fullWidth
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        onClick={togglePasswordVisibility}
                        sx={{ cursor: "pointer" }}
                      >
                        <Box component="span" sx={{ color: "#808080" }}>
                          {showPassword ? (
                            <VisibilityIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          ) : (
                            <VisibilityOffIcon
                              sx={{
                                width: { xs: "15px", md: "20px" },
                                height: { xs: "12px", md: "20px" },
                              }}
                            />
                          )}
                        </Box>
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: "white",
                  borderRadius: "12px",

                  "& .MuiInputBase-root": {
                    height: { xs: "35.39px", md: "48px" },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#6D6D6D",
                    fontSize: { xs: "12px", md: "16px" },
                    fontWeight: "500",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderBottomLeftRadius: "12px",
                      borderBottomRightRadius: "12px",
                      borderTopLeftRadius: "0px",
                      borderTopRightRadius: "0px",
                      borderColor: passwordError.length
                        ? "red"
                        : "#E7E7E7 !important", // This ensures the border is red only if there's an error
                    },
                    "&:hover fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: passwordError.length ? "red" : "#E7E7E7",
                    },
                    "& input": {
                      color: passwordError.length ? "red" : "inherit",
                    },
                    ".css-nxo287-MuiInputBase-input-MuiOutlinedInput-input": {
                      padding: { xs: "16.5px 34px", md: "16.5px 42px" },
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid item>
            {passwordError.length > 0 && (
              <Typography
                sx={{
                  color: "red",
                  fontSize: { xs: "10.38px", md: "14px" },
                  fontWeight: "400",
                }}
              >
                {passwordError.map((error, index) => (
                  <Typography
                    key={index}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ErrorIcon sx={{ width: "13px", height: "13px", mr: 1 }} />
                    {error}
                  </Typography>
                ))}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} mt={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                backgroundColor: "#233F91",
                borderRadius: { xs: "10px", md: "25px" },
                color: "white",
                height: "48px",
                "&:hover": {
                  backgroundColor: "#EEEEEE",
                  color: "#233F91",
                  border: "1.5px solid #233F91",
                },
              }}
            >
              Reset Password
            </Button>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
};

export default ForgetPasswordsForm;
